import type {
  AccountCoverage,
  MonthlyStatus,
} from '@/modules/projects/components/StatementCoverage/types'
import type { DirectoryAccount } from '@/modules/projects/stores/types'
import { accountOfNumbers } from './fromProject'
import { type MonthNumber, monthNumberOf } from './map'
import type { Account } from './types'

// A member card is a second card on the same credit account, like a spouse's card. Its charges are
// printed on the primary card's statement and it has no balance of its own, so it has no pages to
// file. The coverage page gives it a row to show who spent, and so does the Workbench.
export interface MemberCard {
  account: Account
  // The primary card whose statements carry this card's charges.
  parentNumber: string
  months: MonthNumber[]
}

// Kept apart from the ids of real accounts, which are their numbers, so a member row is never a
// place to file pages.
const MEMBER_ID_PREFIX = 'member:'

const isMemberActivity = (month: MonthlyStatus) =>
  month.status === 'member_activity'

// A member card's gaps are the primary card's gaps, and that row already shows them. Only the
// months the card spent in say anything about the card itself.
export const monthsWorthShowing = (row: AccountCoverage) =>
  row.parentId ? row.monthlyStatus.filter(isMemberActivity) : row.monthlyStatus

const memberCardOf = (
  row: AccountCoverage,
  parentNumber: string,
  directory: DirectoryAccount[],
): MemberCard => ({
  account: accountOfNumbers(
    `${MEMBER_ID_PREFIX}${row.id}`,
    [row.id],
    directory,
  ),
  parentNumber,
  months: monthsWorthShowing(row).map((month) => monthNumberOf(month.date)),
})

// The coverage page decides which cards are member cards, so both screens always list the same ones.
export const memberCardsOf = (
  coverage: AccountCoverage[],
  directory: DirectoryAccount[],
): MemberCard[] =>
  coverage
    .flatMap((row) =>
      row.parentId ? [memberCardOf(row, row.parentId, directory)] : [],
    )
    .filter((card) => card.months.length > 0)
